import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const inputCls =
  "w-full bg-transparent border-b border-[#181816]/20 py-3.5 font-light text-lg placeholder:text-[#8A857D]/70 focus:border-[#181816] transition-colors duration-300";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated — sign in with your new password.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="reset-back-to-site" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8A857D] hover:text-[#181816] transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
        <div className="mt-10 border hairline bg-white/60 backdrop-blur px-8 sm:px-10 py-12">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">Studio Portal</p>
          <h1 className="mt-3 font-serif text-3xl sm:text-4xl">Choose a new password.</h1>

          {!token && (
            <p className="mt-8 font-light text-[#B85D3B]" data-testid="reset-missing-token">
              This reset link is missing its token — please use the link from your email.
            </p>
          )}

          {token && done && (
            <div className="mt-8" data-testid="reset-success">
              <p className="font-light text-[#57534E] leading-relaxed">Your password has been updated.</p>
              <Link to="/admin" data-testid="reset-to-login" className="mt-6 inline-flex items-center gap-2 bg-[#181816] text-[#F8F6F0] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#B85D3B] transition-colors duration-300">
                Go to Login
              </Link>
            </div>
          )}

          {token && !done && (
            <form onSubmit={submit} className="mt-10 flex flex-col gap-7" data-testid="reset-form">
              <input
                required
                type="password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password (min 8 characters)"
                data-testid="reset-password-input"
                className={inputCls}
                autoComplete="new-password"
              />
              <input
                required
                type="password"
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                data-testid="reset-confirm-input"
                className={inputCls}
                autoComplete="new-password"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit"
                className="w-full bg-[#181816] text-[#F8F6F0] py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#B85D3B] disabled:opacity-50 transition-colors duration-300"
              >
                {busy ? "Updating…" : "Update Password"}
              </button>
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A857D]">
                <ArrowLeftRight className="h-3 w-3" /> Link valid for one hour
              </p>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
